import React from 'react';
import Uppy from '@uppy/core';
import Dashboard, { DashboardOptions } from '@uppy/dashboard';
import Box from '@uppy/box';
import Dropbox from '@uppy/dropbox';
import GoogleDrive from '@uppy/google-drive';
import TusPatchedPlugin from '../plugins/TusPatch';
import Url from '@uppy/url';
import ImageEditor from '@uppy/image-editor';
import DragDrop from '@uppy/drag-drop';
import { FC, useEffect, useMemo, useCallback } from 'react';
import { type UppyUploaderProps } from '@components/common/uppy/types/UppyTypes';
import { sendAction } from '@helpers/datadog-rum';
import {
  transformUppyUCSResult,
  getValidIdentityToken,
  COMPANION_SANDBOX_URL,
  COMPANION_ALLOWED_HOSTS,
  INVALID_FILENAMES_SET,
  UPLOAD_OVERHEAD_PER_FILE_MS,
  UPLOAD_BASELINE_BYTES_PER_MS,
} from '@helpers/uppy';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/image-editor/dist/style.min.css';
import '@uppy/drag-drop/dist/style.css';

import '@components/common/uppy/styles/uppy_uploader.scss';
import { Result } from '@mocks/uppy-transloadit';
import FileSearchPlugin from '../plugins/Search';

interface BrandfolderTusOptions {
  bearerToken?: string;
  isFl2?: boolean;
  refreshToken?: string;
  version?: string;
  tusUrl?: string;
}

interface ErrorArray {
  error: string[];
}

interface ErrorObject {
  error: string;
}

interface FetchTusSignedUrlOptionsV2 extends Pick<BrandfolderTusOptions, 'bearerToken'> {
  tusUrl: string;
}

interface TusSignedURLsResponse {
  signed_url: string;
}

export const fetchTusSignedUrlV2 = async (options: FetchTusSignedUrlOptionsV2): Promise<string> => {
  try {
    const { bearerToken, tusUrl } = options;

    const response = await fetch(`${tusUrl}/signed_url`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${bearerToken}`
      },
      method: 'GET'
    });

    if (response.ok) {
      const signedUrls = (await response.json()) as TusSignedURLsResponse;
      return signedUrls?.signed_url || '';
    } else {
      const e = (await response.json()) as ErrorArray | ErrorObject;
      throw new Error(Array.isArray(e?.error) ? e.error[0] : e?.error || 'Unknown error');
    }
  } catch (error) {
    console.error(`fetchTusSignedUrlV2`, { error });
    return '';
  }
};

export const UppyUcsUploader: FC<UppyUploaderProps> = ({
  handleUpload,
  uniqIdentifier,
  button = false,
  closeOnComplete = false,
  locale = {
    strings: {
      dropPaste: 'Drag file(s) here or %{browse}',
      dropHereOr: 'Drag file(s) here or %{browse}',
      browse: 'click to browse'
    }
  },
  restrictions = {},
  endpoint,
  template = 'file_ingest',
  trigger = undefined,
  onTriggerClickHandler,
  ucsSdkRef,
  reinitializeSdk,
  inline = false,
  guestUpload,
}) => {
  // useMemo to maintain single instance of Uppy per component lifecycle
  const uppy = useMemo(() => new Uppy({
    allowMultipleUploadBatches: true,
    id: `${uniqIdentifier}-uppy`,
    restrictions: { maxNumberOfFiles: 3000, ...restrictions },
    locale,
    onBeforeFileAdded: (currentFile) => !INVALID_FILENAMES_SET.has(currentFile?.name),
  }), [locale, restrictions, uniqIdentifier]);

  // Create handler function that can be passed to parent components
  const handleTriggerClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const dashboard = uppy.getPlugin('Dashboard') as Dashboard;
    if (dashboard) {
      dashboard.openModal();
    }
  }, [uppy]);

  // Directly assign handler to ref if provided (no useEffect needed)
  // Refs are mutable and can be assigned during render without causing issues
  if (onTriggerClickHandler) {
    onTriggerClickHandler.current = handleTriggerClick;
  }

  // Cleanup: clear ref on unmount
  useEffect(() => {
    return () => {
      if (onTriggerClickHandler) {
        onTriggerClickHandler.current = null;
      }
    };
  }, [onTriggerClickHandler]);

  const templateName = template.replace(/-/g, '_');
  const dashboardOptions: DashboardOptions = {
    browserBackButtonClose: button,
    closeAfterFinish: button && !inline, // Cannot close inline dashboards
    closeModalOnClickOutside: button,
    inline,
    proudlyDisplayPoweredByUppy: false,
    showProgressDetails: true,
    trigger,
    // Only set target for inline mode, and make sure it's valid
    ...(inline && { target: `#${uniqIdentifier}-dashboard` }),
    width: inline ? '100%' : 10,
    showSelectedFiles: true,
    showRemoveButtonAfterComplete: true,
    // Ensure it doesn't auto-open file picker
    note: 'Select files from your computer or connect to external services'
  };

  const handleError = (file, error): void => {
    sendAction('Error Uploading File to Tus', {
      tags: {},
      extra: { file, error }
    });
  };

  useEffect(() => {
    // Track upload session duration and total size for Datadog performance monitoring
    let uploadStartTime: number | null = null;
    let batchTotalBytes = 0;

    uppy.use(TusPatchedPlugin, {
      endpoint: `https://${endpoint}/v2/files/`,
      limit: 4, // no of uploads going at the same time
      parallelUploads: 3,
      onBeforeRequest: async (req) => {
        try {
          const identityToken = await getValidIdentityToken(ucsSdkRef, reinitializeSdk);
          req.setHeader('Authorization', `Bearer ${identityToken}`);
        } catch (err) {
          console.error(err);
        }
      },
      chunkSize: 6 * 1024 * 1024 // 6 MB
    })
      .on('upload', ({ fileIDs }) => {
        // Accumulate across batches in case user adds files mid-upload
        if (uploadStartTime === null) uploadStartTime = Date.now();
        batchTotalBytes += fileIDs.reduce((sum, id) => sum + (uppy.getFile(id)?.size ?? 0), 0);
      })
      .on('cancel-all', () => {
        // Reset so a subsequent upload starts fresh
        uploadStartTime = null;
        batchTotalBytes = 0;
      })
      .on('upload-error', (file, body) => {
        handleError(file, body);
      })
      .on('transloadit:complete', (assembly) => {
        const files = assembly.results[':original']
          || assembly.results[templateName];
        handleUpload(files);
      })
      // eslint-disable-next-line
      .on('complete', async (result) => {
        // Get identity token once for all files
        const identityToken = await getValidIdentityToken(ucsSdkRef, reinitializeSdk);

        // Fetch signed URLs for all successful uploads using the final assembled TUS URL
        await Promise.all(
          (result?.successful || []).map(async (file) => {
            const tusUrl = file?.response?.uploadURL || '';
            const brandfolderTusUrl = await fetchTusSignedUrlV2({
              bearerToken: identityToken,
              tusUrl
            });
            file.meta = { ...file.meta, brandfolderTusUrl };
          })
        ).catch((error: unknown) => {
          console.error('Failed to fetch signed URLs', error);
        });

        // Transform result data to desired format using helper function
        const transformedFiles = await transformUppyUCSResult(result);

        // Send upload performance metrics to Datadog
        if (uploadStartTime !== null) {
          const duration_ms = Date.now() - uploadStartTime;
          const success_count = result?.successful?.length ?? 0;
          const failed_count = result?.failed?.length ?? 0;
          const file_count = success_count + failed_count;
          const expected_duration_ms = (file_count * UPLOAD_OVERHEAD_PER_FILE_MS)
            + Math.round(batchTotalBytes / UPLOAD_BASELINE_BYTES_PER_MS);
          const duration_ratio = Math.round((duration_ms / expected_duration_ms) * 100);
          sendAction('Uppy UCS Upload Completed', {
            duration_ms,
            total_size_bytes: batchTotalBytes,
            file_count,
            success_count,
            failed_count,
            expected_duration_ms,
            duration_ratio,
            brandfolder_id: window.BFG?.brandfolder_key,
            user_id: window.BFG?.currentUser?.user_id,
          });
          // Reset so a subsequent upload starts fresh
          uploadStartTime = null;
          batchTotalBytes = 0;
        }

        handleUpload(transformedFiles as Result[]);
        if (closeOnComplete) {
          uppy.close();
        } else {
          uppy.reset();
        }
      })
      .use(Dashboard, dashboardOptions)
      .use(ImageEditor, {
        target: Dashboard
      })
      .use(FileSearchPlugin, {
        id: 'FileSearchPlugin',
      })
      .use(Box, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: Dashboard
      })
      .use(Url, {
        companionUrl: COMPANION_SANDBOX_URL,
        target: Dashboard
      })
      .use(Dropbox, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: Dashboard
      })
      .use(GoogleDrive, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: Dashboard
      })
      .use(DragDrop, {
        target: trigger,
      });

    const targetElement = document.querySelector(trigger);
    if (targetElement) {
      targetElement.addEventListener('click', (event) => {
        event.preventDefault();
        const dashboard = uppy.getPlugin('Dashboard') as Dashboard;
        if (dashboard) {
          dashboard.openModal();
        }
      });
    }

    function updateDashboardClass() {
      const dashboardEl = document.querySelector('.uppy-Dashboard-files');
      if (!dashboardEl) return;
      const items = dashboardEl.querySelectorAll('.uppy-Dashboard-Item');
      dashboardEl.classList.toggle('single-item', items.length === 1);
    }

    // Attach Uppy event listeners
    uppy.on('file-added', () => {
      // Delay to give Uppy time to render DOM for new file
      requestAnimationFrame(updateDashboardClass);

      // Automatically open the Dashboard modal when files are added
      const dashboard = uppy.getPlugin('Dashboard') as Dashboard;
      if (dashboard && !dashboard.isModalOpen()) {
        dashboard.openModal();
      }
    });

    uppy.on('file-removed', () => {
      requestAnimationFrame(updateDashboardClass);
    });

    // Observe DOM until dashboard is mounted, then run initial update
    const observer = new MutationObserver(() => {
      const dashboardEl = document.querySelector('.uppy-Dashboard-files');
      if (dashboardEl) {
        updateDashboardClass();
        observer.disconnect(); // Stop observing once found
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      uppy.off('file-added', updateDashboardClass);
      uppy.off('file-removed', updateDashboardClass);
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboard: Dashboard = uppy.getPlugin('Dashboard');
  if (dashboard && !inline) {
    // Only manually render for modal dashboards, inline dashboards render automatically
    dashboard.render({});
  }

  return (inline || !trigger) ? (
    <div id={`${uniqIdentifier}-dashboard`} />
  ) : null;
};
